import { useFetch } from "../../hooks/useFetch";
import { money } from "../../lib/api";
import { Loading } from "../../components/States";
import { CCard } from "../../components/customer/ui";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { FolderKanban, FileText, CreditCard, ArrowRight, MapPin, CalendarClock, CheckCircle2, DollarSign, Wrench, Clock } from "lucide-react";

const ACT_ICON = { check: CheckCircle2, dollar: DollarSign, calendar: CalendarClock, wrench: Wrench, file: FileText };
const ACT_TINT = { check: "bg-emerald-50 text-emerald-600", dollar: "bg-emerald-50 text-emerald-600", calendar: "bg-sky-50 text-sky-600", wrench: "bg-blue-50 text-[#1286F2]", file: "bg-amber-50 text-amber-600" };

export default function CustomerHome() {
  const { data, loading } = useFetch("/dashboard/customer");
  const { user } = useAuth();
  const navigate = useNavigate();
  if (loading || !data) return <Loading />;

  const appt = data.next_appointment;
  const apptDate = appt ? new Date(appt.scheduled_date) : null;

  return (
    <div className="space-y-8" data-testid="customer-dashboard">
      <div>
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">Welcome back, {user?.name?.split(" ")[0]}!</h1>
        <p className="mt-2 text-lg text-slate-500">Here's an overview of your projects, appointments, and activity.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <StatCard icon={FolderKanban} value={data.active_projects} label="Active Projects" onClick={() => navigate("/portal/projects")} testid="stat-projects" />
        <StatCard icon={FileText} value={data.open_estimates.length} label="Pending Estimates" onClick={() => navigate("/portal/estimates")} testid="stat-estimates" />
        <StatCard icon={CreditCard} value={money(data.balance_due)} label="Balance Due" onClick={() => navigate("/portal/payments")} testid="stat-balance" />
      </div>

      {/* Next appointment */}
      {appt && (
        <CCard className="overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <h2 className="text-xl font-bold text-slate-900">Your Next Appointment</h2>
            <button onClick={() => navigate("/portal/appointments")} className="text-sm font-semibold text-[#1286F2] hover:underline" data-testid="view-appointments">Manage</button>
          </div>
          <div className="flex flex-col gap-6 p-6 md:flex-row md:items-center" data-testid="next-appointment">
            <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-2xl bg-[#1286F2] text-white">
              <span className="text-xs font-bold uppercase tracking-wide">{apptDate.toLocaleString("en-US", { month: "short" })}</span>
              <span className="text-3xl font-black leading-none">{apptDate.getDate()}</span>
            </div>
            <div className="flex-1">
              <div className="text-xl font-bold text-slate-900">{appt.service_type}</div>
              <div className="mt-1.5 flex items-center gap-2 text-sm text-slate-500"><MapPin size={15} /> {appt.address}</div>
              <div className="mt-1 flex items-center gap-2 text-sm text-slate-500"><Clock size={15} /> {appt.window} · {appt.technician}</div>
            </div>
            <div className="relative h-28 w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100 md:w-48" aria-hidden="true">
              <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(0deg,rgba(148,163,184,.15) 1px,transparent 1px),linear-gradient(90deg,rgba(148,163,184,.15) 1px,transparent 1px)", backgroundSize: "22px 22px" }} />
              <div className="absolute left-1/2 top-1/2 h-16 w-1 -translate-x-1/2 -translate-y-1/2 rotate-[20deg] bg-slate-300/70" />
              <div className="absolute left-[42%] top-[42%] flex -translate-x-1/2 -translate-y-full flex-col items-center">
                <MapPin size={26} className="fill-[#1286F2] text-[#1286F2]" />
              </div>
            </div>
          </div>
        </CCard>
      )}

      {/* Recent activity */}
      <CCard>
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-xl font-bold text-slate-900">Recent Activity</h2>
          <button onClick={() => navigate("/portal/projects")} className="text-sm font-semibold text-[#1286F2] hover:underline" data-testid="activity-view-all">View All</button>
        </div>
        <div className="divide-y divide-slate-100" data-testid="recent-activity">
          {data.recent_activity.length === 0 && <div className="px-6 py-10 text-center text-slate-400">No recent activity yet.</div>}
          {data.recent_activity.map((a, i) => {
            const Ic = ACT_ICON[a.icon] || CheckCircle2;
            return (
              <div key={i} className="flex items-center gap-4 px-6 py-4" data-testid={`activity-${i}`}>
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${ACT_TINT[a.icon] || "bg-slate-100 text-slate-500"}`}><Ic size={18} /></div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-slate-800">{a.label}</div>
                  {a.project && <div className="truncate text-sm text-slate-400">{a.project}</div>}
                </div>
                <div className="shrink-0 text-sm text-slate-400">{new Date(a.date).toLocaleDateString()}</div>
              </div>
            );
          })}
        </div>
      </CCard>
    </div>
  );
}

function StatCard({ icon: Icon, value, label, onClick, testid }) {
  return (
    <button onClick={onClick} className="group rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-[#1286F2]/40 hover:shadow-md" data-testid={testid}>
      <div className="flex items-start justify-between">
        <Icon size={24} className="text-[#1286F2]" />
      </div>
      <div className="mt-4 text-4xl font-black text-slate-900">{value}</div>
      <div className="mt-1 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500">{label}</span>
        <span className="flex items-center gap-1 text-sm font-semibold text-[#1286F2] opacity-0 transition group-hover:opacity-100">View <ArrowRight size={14} /></span>
      </div>
    </button>
  );
}
